/**
 * COMPOSANT: TABLE CELL
 * Cellule de tableau avec truncation et tooltip intelligent
 * Le tooltip ne s'affiche que si le texte est réellement tronqué
 */

import { cn } from '@/lib/utils';
import { useRef, useState } from 'react';

interface TableCellProps {
  children: string | number | null | undefined;
  className?: string;
  align?: 'left' | 'center' | 'right';
  truncate?: boolean; // Active le truncate avec tooltip
}

export function TableCell({
  children,
  className,
  align = 'left',
  truncate = true,
}: TableCellProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const textRef = useRef<HTMLDivElement>(null);

  const content = children ?? '-';
  const stringContent = String(content);

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (truncate && textRef.current) {
      // Détecte si le texte est tronqué en comparant scrollWidth et clientWidth
      const isTruncated = textRef.current.scrollWidth > textRef.current.clientWidth;

      if (isTruncated) {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition({
          x: rect.left,
          y: rect.bottom + 4,
        });
        setShowTooltip(true);
      }
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <>
      <div
        ref={textRef}
        className={cn(
          'text-sm leading-5 text-zinc-900',
          truncate && 'truncate overflow-hidden whitespace-nowrap',
          alignmentClasses[align],
          className
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {stringContent}
      </div>

      {/* Tooltip uniquement si le texte est tronqué */}
      {showTooltip && (
        <div
          className="fixed z-[100] max-w-xs px-3 py-2 text-sm text-white bg-zinc-900 rounded-md shadow-lg pointer-events-none"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
          }}
        >
          {stringContent}
        </div>
      )}
    </>
  );
}
